import React, { useState, useContext } from 'react'
import Svg from '../Svg'
import {
  PARTY_CSS_CLASSES,
  PARTY_FULL_NAMES,
  PARTY_SHORT_NAMES,
} from '../../config'
import { PRIMARY_CANDIDATE_COLORS_2020 } from '../../config/elections/electionConfig2020Primary'
import CandidateImage from '../CandidateImage'
import ElectionContext from '../../context/ElectionContext'
import PropTypes from 'prop-types'

const RaceFull = ({
  candidates,
  name,
  precinctsReportingPct,
  compact,
  level,
  source,
  district,
  addLabel,
  isPrimary: isPrimaryProp,
  previousWinner,
  shortPartyNames,
}) => {
  const context = useContext(ElectionContext)
  const isPrimary = isPrimaryProp || context.isPrimary || undefined
  const [hover, setHover] = useState({ isHover: false, hoveredCandidate: 0 })

  const handleCandidateMouseOver = (candidateId) => {
    setHover({ isHover: true, hoveredCandidate: candidateId })
  }

  const handleCandidateMouseOut = () => {
    setHover({ isHover: false, hoveredCandidate: 0 })
  }

  const hasImages = [
    'usHouse',
    'president',
    'attorneyGeneral',
    'governor',
    'usSenate',
    'mnAuditor',
    'secretaryOfState',
    'primary',
    'mnHouse',
    'mnSenate',
    'local',
  ].some((item) => item === level)

  const getSortedCandidates = () => {
    const length = compact ? 2 : undefined

    return candidates
      ?.sort(function (a, b) {
        if (a.votecount > b.votecount) {
          return -1
        }
        if (a.votecount < b.votecount) {
          return 1
        }
        return 0
      })
      ?.slice(0, length)
  }

  const hasWinner = () => {
    return candidates?.some((candidate) => candidate.winner === true)
  }

  const buildPartyName = (candidate) => {
    let colr = '#333'
    const partyName = compact
      ? candidate.party
      : (shortPartyNames && PARTY_SHORT_NAMES[candidate.party]) ||
        PARTY_FULL_NAMES[candidate.party] ||
        candidate.party
    if (candidate.party === 'Dem') colr = '#4298b5'
    if (candidate.party === 'GOP') colr = '#eb5c40'
    if (
      candidate.winner &&
      previousWinner &&
      previousWinner?.party !== candidate.party
    ) {
      return (
        <>
          {partyName}{' '}
          <span style={{ color: colr }} className="race_party_pickup">
            ({candidate.party} pickup)
          </span>
        </>
      )
    }
    return partyName
  }

  const renderCandidate = (candidate) => {
    const tagClasses = `results_tag ${
      !isPrimary && PARTY_CSS_CLASSES[candidate.party]
    }${hasImages ? ' results_tag-photo' : ''}`
    // once a race is called, the losing candidates are muted
    const outcomeClass = candidate.winner
      ? 'is-winner'
      : hasWinner()
      ? 'is-loser'
      : ''
    const pctClasses = `results_pct ${
      candidate.winner ? 'results_pct_win' : ''
    } ${source}`
    const candidateImage = (
      <CandidateImage
        candidate={candidate}
        date={context.date}
        isPrimary={isPrimary}
        hasImages={hasImages}
      />
    )

    let lastNameArray = candidate.name.split(' ')
    let lastName = lastNameArray[lastNameArray.length - 1].toLowerCase()
    let color = PRIMARY_CANDIDATE_COLORS_2020[lastName]

    const candidateBarStyle = {
      backgroundColor: color,
    }

    return (
      <li
        className={`results_item ${
          hover.hoveredCandidate === candidate.candidateId ? 'is-selected' : ''
        } ${outcomeClass}`}
        onMouseOver={() => handleCandidateMouseOver(candidate.candidateId)}
        onFocus={() => handleCandidateMouseOver(candidate.candidateId)}
        onMouseOut={() => handleCandidateMouseOut()}
        onBlur={() => handleCandidateMouseOut()}
        key={candidate.candidateId}
      >
        <div className="results_item_tag">
          <div className={tagClasses} style={isPrimary && candidateBarStyle}>
            {candidateImage}
            {!isPrimary && (
              <span className="results_tag_party">{candidate.party}</span>
            )}
          </div>
        </div>
        <div className="results_stats_left">
          <h4 className="hdg hdg-3">{candidate.name}</h4>
          {!isPrimary && (
            <div className="results_text">{buildPartyName(candidate)}</div>
          )}
        </div>
        <div className="results_stats_right">
          <div className={pctClasses}>
            {candidate.winner && (
              <Svg icon="checkmark" origHeight={13} origWidth={17} />
            )}
          </div>
          <div className="results_text">
            <span className="candidate_votes_em candidate_votes">
              {`${Math.round(candidate.votepct * 100)}%`}
            </span>
            <span className="candidate_votes candidate_votes_count">
              {candidate.votecount > 0 &&
                `${candidate.votecount.toLocaleString()} ${
                  candidate.votecount === 1 ? 'vote' : 'votes'
                }`}
            </span>
          </div>
        </div>
      </li>
    )
  }

  const buildCandidateNodes = () => {
    return getSortedCandidates()?.map((candidate) => renderCandidate(candidate))
  }

  const buildPrimaryColumns = () => {
    const sorted = getSortedCandidates() || []
    const columns = [
      {
        key: 'Dem',
        heading: (shortPartyNames && PARTY_SHORT_NAMES.Dem) || 'Democratic',
        candidates: [],
      },
      { key: 'GOP', heading: 'Republican', candidates: [] },
      { key: 'Other', heading: 'Other', candidates: [] },
    ]

    sorted.forEach((candidate) => {
      const column =
        columns.find((col) => col.key === candidate.party) ||
        columns.find((col) => col.key === 'Other')
      column.candidates.push(candidate)
    })

    return (
      <div className="results_primaryColumns">
        {columns
          .filter((col) => col.candidates.length > 0)
          .map((col) => (
            <div className="results_primaryColumn" key={col.key}>
              <h4 className="hdg hdg-4 results_primaryColumn_heading">
                {col.heading}
              </h4>
              <ol className={resultsClass()}>
                {col.candidates.map((candidate) => renderCandidate(candidate))}
              </ol>
            </div>
          ))}
      </div>
    )
  }

  const buildCandidateBars = () => {
    return getSortedCandidates()?.map((candidate) => {
      const raceBarStyle = {
        width: candidate.votepct * 100 + '%',
      }

      const raceBarClass = `graph_segment noPointer ${
        !isPrimary && PARTY_CSS_CLASSES[candidate.party]
      } ${
        hover.hoveredCandidate === candidate.candidateId ? 'is-selected' : ''
      } ${hover.isHover ? 'is-hover' : ''}`

      return (
        <div
          onMouseOver={() => handleCandidateMouseOver(candidate.candidateId)}
          onFocus={() => handleCandidateMouseOver(candidate.candidateId)}
          onMouseOut={() => handleCandidateMouseOut()}
          onBlur={() => handleCandidateMouseOut()}
          key={candidate.candidateId}
          className={raceBarClass}
          style={raceBarStyle}
        >
          <span className="invisible">
            {`${candidate.name}: ${Math.round(candidate.votepct * 100)}%`}
          </span>
        </div>
      )
    })
  }

  const buildRaceTitle = () => {
    if (compact) {
      return <h3 className="hdg hdg-3">{district}</h3>
    }
    // e.g. "Governor, Democratic-Farmer-Labor Primary" -> "Governor, DFL Primary"
    const raceName = shortPartyNames
      ? name?.replace(PARTY_FULL_NAMES.Dem, PARTY_SHORT_NAMES.Dem)
      : name
    return (
      <h3 className="hdg hdg-3">
        {raceName}
        {addLabel && addLabel}
      </h3>
    )
  }

  const buildRaceGraph = () => {
    if (!compact && !isPrimary) {
      return (
        <div className="race_graph">
          <div className="graph">{buildCandidateBars()}</div>
        </div>
      )
    }
  }

  const resultsClass = () => {
    return compact ? 'results results-compact' : 'results'
  }

  const buildRaceBody = () => {
    if (isPrimary) {
      return buildPrimaryColumns()
    }
    return <ol className={resultsClass()}>{buildCandidateNodes()}</ol>
  }

  const raceClasses = `race race-full race-card ${hasWinner() && 'has-winner'}${
    isPrimary ? ' race-primary' : ''
  }`

  if (Math.round(precinctsReportingPct * 100) === 0) {
    return (
      <div className={raceClasses} data-testid="race-full">
        <div className="race_header">
          <div className="split">
            <div className="split_left">{buildRaceTitle()}</div>
            <div className="split_right">
              {Math.round(precinctsReportingPct * 100)}% Reporting
            </div>
          </div>
        </div>
        {buildRaceGraph()}
        <div className="race_body">
          {name ? buildRaceBody() : <p>Polls close at 8 pm.</p>}
        </div>
      </div>
    )
  }

  return (
    <div className={raceClasses} data-testid="race-full">
      <div className="race_header">
        <div className="split">
          <div className="split_left">{buildRaceTitle()}</div>
          <div className="split_right">
            {Math.round(precinctsReportingPct * 100)}% Reporting
          </div>
        </div>
      </div>
      {buildRaceGraph()}
      <div className="race_body">
        {Math.round(precinctsReportingPct * 100) === 0 ? (
          <p>Polls close at 8 pm on election night.</p>
        ) : (
          buildRaceBody()
        )}
      </div>
    </div>
  )
}

RaceFull.propTypes = {
  candidates: PropTypes.array.isRequired,
  name: PropTypes.string,
  isPrimary: PropTypes.bool,
  precinctsReportingPct: PropTypes.number.isRequired,
  compact: PropTypes.bool,
  level: PropTypes.string,
  source: PropTypes.string,
  district: PropTypes.string,
  addLabel: PropTypes.string,
  previousWinner: PropTypes.object,
  shortPartyNames: PropTypes.bool,
}

export default RaceFull
